import { AttachmentPreviewButton } from '@/shared/components/documents/AttachmentPreviewButton';
// BizPlus parity Phase 1.4 — shared document-conversion toolbar.
//
// Renders exactly the transitions the SERVER says this user may perform on
// this document right now. The list is never hardcoded per page: it comes from
// GET /api/documents/transitions/:type/:id, which intersects the shared
// registry (src/shared/documents/transitions.js) with the caller's permissions
// and the document's current status. A button that appears here will not 403.

import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowRight, Check, Pencil, X } from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { cn, localToday } from '@/shared/utils/utils';
import { mergeTransporterNames } from '@/shared/constants/transporters';
import { useMasterItems } from '@/features/master-data/hooks/useMasters';
import { TransporterDefineModal, useTransporters } from './TransporterDefineModal';

export interface AvailableTransition {
  to: string;
  label: string;
  permission: string;
  alreadyDone: boolean;
  handoffComplete?: boolean;
  targetCreated?: boolean;
  /** delivery_challan only — a real DC/FY/NNNN has already been minted, as
   * opposed to the DRAFT-xxxx placeholder every hand-off raises. */
  challanIssued?: boolean;
}

/**
 * Whether "Create Tax Invoice" may still be pressed on this document.
 *
 * That action does not go through the transition registry (it calls
 * /api/invoice-requests directly), so the server answers for it separately —
 * see taxInvoiceAvailability in src/api-documents/transitions.js. `null` for a
 * document type that has no such action.
 *
 *   not_accepted     the customer has not accepted it yet — the same gate the
 *                    proforma and sales-order conversions already use
 *   proforma_exists  a proforma stands between this quotation and the bill
 *   already_invoiced an invoice, draft or final, already exists for it
 *   closed           cancelled, expired or superseded
 */
export interface TaxInvoiceAvailability {
  allowed: boolean;
  blockedBy: '' | 'not_accepted' | 'proforma_exists' | 'already_invoiced' | 'closed';
  reason: string;
  invoiceId?: string;
  invoiceNumber?: string;
  proformaId?: string;
  proformaNumber?: string;
}

// Transitions the server accepts extra caller-supplied data for
// (transitions.js's requiredExtraFields/extraFields) — these open a small
// capture form instead of firing immediately, since the server will 400
// without the required field.
interface PackagingPreviewLine {
  key: string;
  description: string;
  /** '' when the product's pack size falls outside every agreed band. */
  packaging: string;
  packSize?: string;
  quantity: number;
}

/** Total pieces to load, by packaging type — e.g. { packaging: 'box', quantity: 45 }. */
interface PackagingQuantityTotal {
  packaging: string;
  quantity: number;
}

interface FleetDriver {
  _id: string;
  userId: string;
  name: string;
  vehicleNumber: string;
  vehicleType?: string;
  status: string;
}

type FormVals = Record<string, string>;
interface ExtraField {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  /** Shown only while this is true (e.g. cheque no. only for Cheque). */
  showWhen?: (v: FormVals) => boolean;
  /** Required only while this is true (and shown). */
  requiredWhen?: (v: FormVals) => boolean;
}

// Payment at hand-off (client, 2026-10-05) — mirrors src/shared/sales/handoff-payment.js.
const isCreditTerms = (v: FormVals) => String(v.paymentTerms || '').startsWith('credit');
const hasMethod = (v: FormVals) => ['full', 'partial'].includes(String(v.paymentTerms || ''));
// The client's payment proof is compulsory for every payment (client,
// 2026-10-06) — cash and card too; credit terms need none.
const PROOF_METHODS = ['bank_transfer', 'upi', 'cheque', 'cash', 'credit_card', 'debit_card', 'other'];

const EXTRA_INPUT_TARGETS: Record<string, { title: string; fields: ExtraField[] }> = {
  sales_order: {
    title: 'Convert to Sales Order',
    fields: [
      // Captured when there IS one, never demanded: most RBM orders are
      // confirmed on a phone call with no PO document anywhere. Where a PO
      // genuinely applies, the pipeline record's own opt-in flag makes it
      // mandatory (src/shared/sales/customer-po.js), and a number entered here
      // is written back onto that record so the same fact is typed once.
      { key: 'customerPoNumber', label: "Customer's PO Number (if there is one)", type: 'text' },
      { key: 'customerPoDate', label: "Customer's PO Date", type: 'date' },
      // The PO document itself. It lands on the sales order AND is written back
      // onto the pipeline record, so Dispatch can open the customer's actual
      // purchase order without CRM access and the rep finds it where they left
      // the rest of the record.
      { key: 'customerPoAttachments', label: "Customer's PO Document", type: 'files' },
    ],
  },
  delivery_challan: {
    title: 'Hand off to Dispatch',
    // None of these are required here — the Dispatch team can still set them
    // from their own screen after hand-off (see the transition's own comment
    // in shared/documents/transitions.js). Vocabulary matches PACKAGING_TYPES
    // (src/shared/dispatch/stages.js) exactly, so a value this form submits
    // is always one the schema enum accepts.
    fields: [
      // NO PACKAGING DROPDOWN. Packaging is derived per line from each
      // product's pack size (src/shared/dispatch/packaging.js) and shown here
      // read-only: the answer is already in the catalogue, and one value for a
      // whole order can only ever be right about part of a mixed consignment.
      { key: 'createDeliveryChallan', label: 'Create Delivery Challan?', type: 'select', options: [{ value: 'yes', label: 'Yes — create it now' }, { value: 'no', label: 'No' }] },
      { key: '__packaging', label: 'Packaging', type: 'packaging-preview' },
      { key: 'paymentTerms', label: 'Payment terms', type: 'select', required: true, options: [
        { value: '', label: 'Select payment terms' },
        { value: 'full', label: 'Full Payment / Advance' },
        { value: 'partial', label: 'Partial Payment' },
        { value: 'credit_15', label: 'Credit — 15 days' },
        { value: 'credit_30', label: 'Credit — 30 days' },
        { value: 'credit_45', label: 'Credit — 45 days' },
        { value: 'credit_60', label: 'Credit — 60 days' },
        { value: 'credit_custom', label: 'Credit — custom days' },
      ] },
      { key: 'creditDays', label: 'Credit days', type: 'number',
        showWhen: (v) => v.paymentTerms === 'credit_custom', requiredWhen: (v) => v.paymentTerms === 'credit_custom' },
      { key: 'partialAmount', label: 'Amount received (partial)', type: 'number',
        showWhen: (v) => v.paymentTerms === 'partial', requiredWhen: (v) => v.paymentTerms === 'partial' },
      { key: 'paymentMethod', label: 'Payment method', type: 'select', options: [
        { value: '', label: 'Select payment method' },
        { value: 'bank_transfer', label: 'Bank Transfer / NEFT / RTGS' },
        { value: 'upi', label: 'UPI' },
        { value: 'cheque', label: 'Cheque' },
        { value: 'cash', label: 'Cash' },
        { value: 'credit_card', label: 'Credit Card' },
        { value: 'debit_card', label: 'Debit Card' },
        { value: 'other', label: 'Other — specify' },
      ], showWhen: hasMethod, requiredWhen: hasMethod },
      { key: 'paymentMethodOther', label: 'Specify payment method', type: 'text',
        showWhen: (v) => hasMethod(v) && v.paymentMethod === 'other', requiredWhen: (v) => hasMethod(v) && v.paymentMethod === 'other' },
      { key: 'chequeNo', label: 'Cheque number', type: 'text',
        showWhen: (v) => hasMethod(v) && v.paymentMethod === 'cheque', requiredWhen: (v) => hasMethod(v) && v.paymentMethod === 'cheque' },
      { key: 'chequeBank', label: 'Cheque bank', type: 'text', showWhen: (v) => hasMethod(v) && v.paymentMethod === 'cheque' },
      { key: 'chequeDate', label: 'Cheque date', type: 'date', showWhen: (v) => hasMethod(v) && v.paymentMethod === 'cheque' },
      { key: 'paymentReference', label: 'UTR / transaction reference', type: 'text',
        showWhen: (v) => hasMethod(v) && ['bank_transfer', 'upi', 'credit_card', 'debit_card'].includes(v.paymentMethod) },
      // Never typed — Cash/UPI/IMPS-NEFT assume the full order total was
      // received, Credit derives it as total minus the credit amount above.
      // See the 'amount-derived' render branch and derivedAmountReceived.
      { key: 'amountReceived', label: 'Amount received', type: 'amount-derived' },
      { key: 'paymentProofs', label: 'Payment proof (images or PDF)', type: 'proof-files' },
      { key: 'transportMode', label: 'Transport', type: 'select', required: true, options: [
        { value: '', label: 'Select transport' },
        // Sales picks only HOW it goes (client, 2026-10-08): local transport, or
        // a third-party transporter such as ACPL. Vehicle, driver, LR and
        // tracking are filled by Dispatch from their own screen. "In-house
        // driver" is no longer offered; it is kept for hand-offs that already
        // used it (shown only when editing one of those).
        { value: 'local_transport', label: 'Local transport (tempo / Porter) — Dispatch fills the details' },
        { value: 'third_party_courier', label: 'Third-party transporter (ACPL etc.) — Dispatch fills the details' },
        { value: 'in_house_driver', label: 'In-house driver (old hand-offs only)' },
      ] },
      // Checklist Item 55: "Handoff - Dropdown to select (Mandatory Field) - Paid, To Pay, Self, Add new"
      { key: 'freightPayment', label: 'Freight / Transport Terms (Mandatory)', type: 'select', required: true, options: [
        { value: '', label: 'Select terms (Paid, To Pay, Self, Add new)' },
        { value: 'paid', label: 'Paid' },
        { value: 'to_pay', label: 'To Pay' },
        { value: 'self', label: 'Self' },
        { value: 'custom', label: '+ Add new' },
      ] },
      { key: 'freightPaymentCustom', label: 'Specify Freight Payment Term', type: 'text',
        showWhen: (v) => v.freightPayment === 'custom', requiredWhen: (v) => v.freightPayment === 'custom' },
      // Sales picks ONLY the third-party transporter and its terms (client,
      // 2026-10-23): which local tempo / Porter takes the goods to that
      // transporter, its vehicle, phone and cost are Dispatch's — filled on
      // their Transport Details form — and the amount is Admin's. The
      // local-transport block and the driver picker that used to sit here are
      // gone; the driver stays only for an old in-house-driver hand-off.
      { key: 'driverId', label: 'Assign Driver & Vehicle', type: 'driver',
        showWhen: (v) => v.transportMode === 'in_house_driver', requiredWhen: (v) => v.transportMode === 'in_house_driver' },
      { key: 'courierName', label: 'Courier / transporter name', type: 'transporter',
        showWhen: (v) => v.transportMode === 'third_party_courier', requiredWhen: (v) => v.transportMode === 'third_party_courier' },
      { key: 'note', label: 'Note for Dispatch', type: 'text' },
      { key: 'requiredDispatchDate', label: 'Dispatch Due Date', type: 'date', required: true },
      // Only shown (and only required, server-side) when editing a hand-off
      // ALREADY submitted — see isEditingOpenHandoff below. The first hand-off
      // needs no justification; changing one Dispatch may already be acting
      // on does, unless an Admin/Superadmin is the one making the change.
      { key: 'reason', label: 'Reason for this change', type: 'text' },
    ],
  },
};

/** What Sales actually submitted on a prior hand-off — used to pre-fill the
 *  dialog when they re-open it to edit rather than starting from scratch. */
export interface ExistingHandoff {
  paymentArrangement: string;
  transportMode: string;
  driverId: string;
  courierName: string;
  creditAmount: string;
  note: string;
  requiredDispatchDate: string;
  requiredDeliveryDate: string;
  createDeliveryChallan: 'yes' | 'no';
  paymentProofs: { url: string; name: string }[];
  freightPayment?: string;
  freightPaymentCustom?: string;
  localTransportUsed?: string;
  localTransportName?: string;
  localTransportNameCustom?: string;
  localTransportMobile?: string;
  localTransportVehicle?: string;
  localTransportPayment?: string;
  localTransportAmount?: string;
  paymentTerms?: string;
  creditDays?: string;
  partialAmount?: string;
  paymentMethod?: string;
  paymentMethodOther?: string;
  paymentReference?: string;
  chequeNo?: string;
  chequeBank?: string;
  chequeDate?: string;
}

export function useDocumentTransitions(docType: string, docId: string | null) {
  return useQuery({
    queryKey: ['document-transitions', docType, docId],
    queryFn: async () => {
      const { data } = await api.get<{
        status: string;
        transitions: AvailableTransition[];
        taxInvoice: TaxInvoiceAvailability | null;
        handoffTotal?: number;
        handoffCurrency?: string;
        handoffAmountReceived?: number;
        existingHandoff?: ExistingHandoff | null;
      }>(`/api/documents/transitions/${docType}/${docId}`);
      return data;
    },
    enabled: !!docId,
  });
}

export function useRunTransition(docType: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, target, body }: { id: string; target: string; body?: Record<string, unknown> }) => {
      const { data } = await api.post(`/api/documents/transitions/${docType}/${id}/to/${target}`, body || {});
      return data as {
        ok: boolean; document: { _id: string }; warning?: string;
        paymentPosting?: { posted: boolean; reason?: string; paymentNumber?: string; amount?: number };
        // Set instead of ok/document when a non-admin edited a hand-off
        // already submitted to Dispatch — the change is parked in the
        // Invoice Edit Approvals queue, not applied (src/api-documents/
        // transitions.js's dispatchAction:'handoff' branch).
        pendingApproval?: boolean; requestId?: string; message?: string;
      };
    },
    onSuccess: () => {
      // A conversion changes both documents, so refresh broadly rather than
      // trying to guess which lists the new target belongs to.
      qc.invalidateQueries({ queryKey: ['document-transitions'] });
      qc.invalidateQueries({ queryKey: ['quotations'] });
      qc.invalidateQueries({ queryKey: ['proforma'] });
      qc.invalidateQueries({ queryKey: ['invoices-direct'] });
      qc.invalidateQueries({ queryKey: ['invoice-requests'] });
      qc.invalidateQueries({ queryKey: ['sales-ops'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['sales-doc'] });
    },
  });
}

interface DocumentActionsProps {
  docType: string;
  docId: string;
  /** Called after a successful conversion, with the target type. */
  onConverted?: (target: string) => void;
  size?: 'sm' | 'md';
  /**
   * Re-word a transition's button for the page it's rendered on, keyed by
   * `to` (the target document type). The label itself comes from the shared
   * registry (src/shared/documents/transitions.js) and is written for the
   * SOURCE team's point of view — "Hand off to Dispatch" is correct on the
   * Sales team's own orders screen, where they really are handing off to
   * someone else. On the Dispatch team's OWN queue, the same transition reads
   * as talking to themselves, so DispatchOrdersPage overrides it here rather
   * than the registry gaining a second, page-aware label for one transition.
   */
  labelOverrides?: Record<string, string>;
}

export function DocumentActions({ docType, docId, onConverted, size = 'sm', labelOverrides }: DocumentActionsProps) {
  const { data, isLoading } = useDocumentTransitions(docType, docId);
  const run = useRunTransition(docType);
  const [openTarget, setOpenTarget] = useState<AvailableTransition | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  /** Uploaded PO documents, kept apart from the string-valued fields. */
  const [formFiles, setFormFiles] = useState<Record<string, { url: string; name: string }[]>>({});
  const [uploading, setUploading] = useState(false);

  // Loaded only while the challan dialog is open — every other document action
  // has no use for either, and a driver list fetched on every Dispatch Orders
  // row would be one request per row.
  const challanDialogOpen = openTarget?.to === 'delivery_challan';

  const { data: packagingPreviewData } = useQuery({
    queryKey: ['documents', 'packaging-preview', docType, docId],
    queryFn: async () => {
      const { data } = await api.get<{ lines: PackagingPreviewLine[]; summary: PackagingQuantityTotal[]; customer: { name: string; phone: string; gstin: string; shippingAddress: string } }>(
        `/api/documents/transitions/${docType}/${docId}/packaging-preview`,
      );
      return { lines: data.lines ?? [], summary: data.summary ?? [], customer: data.customer };
    },
    enabled: challanDialogOpen,
    staleTime: 60_000,
  });
  const packagingPreview = packagingPreviewData?.lines ?? [];
  const packagingTotals = packagingPreviewData?.summary ?? [];
  const handoffTotal = Number(data?.handoffTotal) || 0;
  // A USD/EUR order's hand-off amounts are in that currency, not rupees.
  const handoffCurrency = String(data?.handoffCurrency || 'INR').toUpperCase();
  const money = (n: number) => handoffCurrency === 'INR'
    ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
    : `${handoffCurrency === 'USD' ? '$' : handoffCurrency === 'EUR' ? '€' : handoffCurrency === 'GBP' ? '£' : handoffCurrency + ' '}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const paymentArrangementValue = String(formValues.paymentArrangement || '').trim().toLowerCase();
  // Never typed: Cash/UPI/IMPS-NEFT assume the full order total was
  // received; Credit derives it as total minus whatever credit is extended.
  // The server independently recomputes the same thing (transitions.js) and
  // never trusts a client-supplied amountReceived.
  // Full = whole total; Partial = the amount typed; Credit = nothing yet.
  // The server works out the same (src/shared/sales/handoff-payment.js).
  const partialValue = Number(formValues.partialAmount || 0);
  const derivedAmountReceived = !formValues.paymentTerms
    ? 0
    : isCreditTerms(formValues)
      ? 0
      : formValues.paymentTerms === 'partial'
        ? Math.max(0, Math.min(handoffTotal, Number.isFinite(partialValue) ? partialValue : 0))
        : handoffTotal;
  const balanceAmount = Math.max(0, handoffTotal - derivedAmountReceived);
  const paymentProofRequired = challanDialogOpen
    && hasMethod(formValues)
    && PROOF_METHODS.includes(String(formValues.paymentMethod || ''));
  const fieldShown = (fld: ExtraField) => !fld.showWhen || fld.showWhen(formValues);
  const fieldRequired = (fld: ExtraField) => fieldShown(fld) && Boolean(fld.required || fld.requiredWhen?.(formValues));
  const hasPaymentProof = (formFiles.paymentProofs || []).length > 0;

  const { data: driversData } = useQuery({
    queryKey: ['fleet', 'drivers', 'available'],
    queryFn: async () => {
      const { data } = await api.get<{ drivers: FleetDriver[] }>('/api/fleet/drivers');
      // Only drivers who can actually take this load; assigning a busy one is
      // refused by the server anyway (409), so offering them wastes a click.
      return (data.drivers ?? []).filter((d) => d.status === 'available');
    },
    enabled: challanDialogOpen,
    staleTime: 30_000,
  });
  const drivers = driversData ?? [];
  const { data: transportationMasters = [] } = useMasterItems('transportation', undefined, true);
  const { data: definedTransporters = [] } = useTransporters();
  const transporterOptions = useMemo(
    () => mergeTransporterNames([...transportationMasters.map((item) => item.name), ...definedTransporters.map((t) => t.name)]),
    [transportationMasters, definedTransporters],
  );
  // The transporter being defined (its name) — '' for a brand-new one, null when closed.
  const [defineTransporter, setDefineTransporter] = useState<string | null>(null);
  // run.isPending only disables Confirm after a re-render; two quick clicks
  // both got through and each parked its own hand-off approval request.
  const runningRef = useRef(false);
  const selectedTransporterDetails = (name?: string) =>
    definedTransporters.find((t) => t.name.toLowerCase() === String(name || '').toLowerCase()) || null;

  // "Raise invoice request" is not offered anywhere.
  //
  // It creates a real InvoiceRequest, but one with no invoice behind it yet —
  // and the Invoices list only shows requests that already have an invoice
  // (`.filter(r => r.invoiceId)`), with no screen for pending ones. So the
  // action succeeded and then vanished, which reads as a broken button.
  //
  // "Create Tax Invoice" is the single route to an invoice: it raises the
  // request AND generates the draft in one step, landing on the Invoices list
  // with a Finalize & Post action. It calls POST /api/invoice-requests
  // directly, not this registry, so it is unaffected by this.
  //
  // The transition itself is left in the registry — the API can still be
  // driven by an integration, and an approval-based flow can be reinstated by
  // deleting these two lines and building the screen it needs.
  const transitions = (data?.transitions ?? []).filter((t) => t.to !== 'invoice_request');

  if (isLoading || transitions.length === 0) return null;

  const labelFor = (t: AvailableTransition) => labelOverrides?.[t.to] ?? t.label;

  const runNow = (t: AvailableTransition, body?: Record<string, unknown>) => {
    if (runningRef.current) return;
    runningRef.current = true;
    run.mutate(
      { id: docId, target: t.to, body: t.to === 'delivery_challan' ? { ...body, dispatchAction: 'handoff' } : body },
      {
        onSuccess: (res) => {
          // A non-admin's edit to an already-submitted hand-off was parked
          // for Admin/Superadmin approval, not applied — say so honestly
          // instead of claiming the change already went through, and skip
          // every side-effect below that assumes it did.
          if (res.pendingApproval) {
            toast(res.message || 'Submitted for Admin/Superadmin approval — not applied yet.', { icon: '⏳', duration: 9000 });
            setOpenTarget(null);
            setFormValues({});
            setFormFiles({});
            return;
          }
          // labelFor(), not t.label — the Dispatch screen overrides "Hand off
          // to Dispatch" and the toast has to say what the button said.
          toast.success(`${labelFor(t)} — done.`);
          // Raising a Sales Order also moves the pipeline record to Order
          // Confirmed. When that part could not happen — a record marked
          // PO-backed with no PO number — the document still stands and the
          // reason is reported rather than swallowed.
          const warning = (res as { warning?: string })?.warning;
          if (warning) toast(warning, { icon: '⚠️', duration: 9000 });
          // The receipt this hand-off just posted against the invoice — see
          // recordDispatchHandoffReceipt (api-accounts/sales-invoices.js).
          // Absent entirely for a Credit arrangement or a non-invoice order,
          // so this only fires when there was real money to report.
          const posting = (res as { paymentPosting?: { posted: boolean; reason?: string; paymentNumber?: string; amount?: number } })?.paymentPosting;
          if (posting?.posted) {
            toast.success(`Payment ${posting.paymentNumber} recorded (₹${(posting.amount ?? 0).toLocaleString('en-IN')}).`);
          } else if (posting && !posting.posted && posting.reason && posting.reason !== 'Invoice already fully paid.') {
            toast(`Handed off, but the payment could not be recorded in Accounts: ${posting.reason}`, { icon: '⚠️', duration: 9000 });
          }
          onConverted?.(t.to);
          setOpenTarget(null);
          setFormValues({});
          setFormFiles({});
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Conversion failed.')),
        onSettled: () => { runningRef.current = false; },
      },
    );
  };

  const extraInputSpec = openTarget ? EXTRA_INPUT_TARGETS[openTarget.to] : null;
  // Same condition as isEditableHandoff below (computed per-button, in the
  // list this dialog was opened from) — re-derived here for the dialog's own
  // scope, which only has openTarget, not the specific `t` that was clicked.
  const isEditingOpenHandoff = openTarget?.to === 'delivery_challan' && Boolean(openTarget?.handoffComplete);

  return (
    <>
      {transitions.map((t) => {
        // Every OTHER transition is genuinely once-only (a quotation becomes
        // ONE proforma, not two) — `alreadyDone` correctly disables those for
        // good. The hand-off is different: the server already treats a
        // repeat submission as an update (it re-saves dispatchHandoff and
        // re-assigns the driver without double-posting the payment or
        // duplicating the dispatch record — see hooks.confirmSource's sibling
        // in transitions.js), so locking the button once handed off only
        // stopped Sales from fixing a wrong transport mode or driver, with no
        // way to correct it short of an engineer editing the database.
        const isEditableHandoff = t.to === 'delivery_challan' && t.handoffComplete;
        const existing = data?.existingHandoff;
        return (
          <Button
            key={t.to}
            size={size}
            variant="secondary"
            disabled={(t.alreadyDone && !isEditableHandoff) || run.isPending}
            title={isEditableHandoff ? 'Edit hand-off details' : t.alreadyDone ? 'Already converted' : labelFor(t)}
            onClick={(e) => {
              e.stopPropagation();
              const spec = EXTRA_INPUT_TARGETS[t.to];
              if (spec) {
                const todayStr = localToday();
                if (t.to === 'delivery_challan' && isEditableHandoff && existing) {
                  const { paymentProofs, ...existingValues } = existing;
                  setFormValues({
                    // Re-open pre-filled with what was actually submitted
                    // last time, not the blank defaults — editing means
                    // correcting one field, not retyping everything.
                    ...existingValues,
                    localTransportUsed: existingValues.localTransportUsed || 'yes',
                    requiredDispatchDate: existingValues.requiredDispatchDate || todayStr,
                  });
                  // Carried forward as-is so the proof-required check below
                  // doesn't force re-uploading evidence already on file —
                  // Sales can still attach a new one to replace it.
                  setFormFiles({ paymentProofs });
                } else {
                  setFormValues(t.to === 'delivery_challan' ? {
                    // A Dispatch hand-off normally needs its DC immediately. The
                    // operator can still opt out for the exceptional case.
                    createDeliveryChallan: 'yes',
                    creditAmount: handoffTotal.toFixed(2),
                    requiredDispatchDate: todayStr,
                    // Not asked on the form: Dispatch records the local leg
                    // (or "Self" / direct) before marking it dispatched.
                    localTransportUsed: 'yes',
                  } : {});
                  setFormFiles({});
                }
                setOpenTarget(t);
              } else {
                runNow(t);
              }
            }}
          >
            {isEditableHandoff ? <Pencil className="h-3.5 w-3.5 mr-1" /> : t.alreadyDone ? <Check className="h-3.5 w-3.5 mr-1" /> : null}
            {isEditableHandoff ? 'Edit Hand-off' : labelFor(t)}
            {(!t.alreadyDone || isEditableHandoff) && <ArrowRight className="h-3.5 w-3.5 ml-1" />}
          </Button>
        );
      })}

      {openTarget && extraInputSpec && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4"
          onClick={(e) => { e.stopPropagation(); setOpenTarget(null); }}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-sm flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between p-5 pb-0">
              <h3 className="text-sm font-bold text-slate-900">
                {openTarget ? labelFor(openTarget) : extraInputSpec.title}
              </h3>
              <button onClick={() => setOpenTarget(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 overflow-y-auto p-5">
              {challanDialogOpen && packagingPreviewData?.customer && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-1">
                  <p className="font-bold">{packagingPreviewData.customer.name}</p>
                  <p>{packagingPreviewData.customer.phone || 'Phone not recorded'}</p>
                  <p>{packagingPreviewData.customer.shippingAddress || 'Shipping address not recorded'}</p>
                  {packagingPreviewData.customer.gstin && <p>GSTIN: {packagingPreviewData.customer.gstin}</p>}
                </div>
              )}
              {extraInputSpec.fields
                .filter((f) => f.key !== 'creditAmount' || paymentArrangementValue === 'credit')
                .filter((f) => f.key !== 'reason' || isEditingOpenHandoff)
                .filter((f) => fieldShown(f))
                .map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    {f.label}{(fieldRequired(f) || (f.key === 'paymentProofs' && paymentProofRequired)) && <span className="text-rose-500"> *</span>}
                  </label>
                  {f.key === 'driverId' && formValues.transportMode !== 'in_house_driver' ? null : f.key === 'courierName' && formValues.transportMode !== 'third_party_courier' ? null : f.type === 'files' || f.type === 'proof-files' ? (
                    <div className="space-y-1">
                      <input
                        type="file"
                        multiple
                        accept={f.type === 'proof-files' ? 'image/png,image/jpeg,image/webp,application/pdf' : undefined}
                        disabled={uploading}
                        onChange={async (e) => {
                          const chosen = Array.from(e.target.files || []);
                          e.target.value = '';
                          if (chosen.length === 0) return;
                          setUploading(true);
                          try {
                            const added: { url: string; name: string }[] = [];
                            for (const file of chosen) {
                              // Mirrors the server's own multer limit, and the
                              // pipeline record's PO uploader.
                              if (file.size > 50 * 1024 * 1024) {
                                toast.error(`"${file.name}" is larger than 50 MB.`);
                                continue;
                              }
                              const body = new FormData();
                              body.append('file', file);
                              const { data } = await api.post<{ url: string }>('/api/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
                              if (data?.url) added.push({ url: data.url, name: file.name });
                            }
                            // One write for the whole batch: writing per file
                            // drops uploads, because each callback closes over
                            // the value it started with.
                            if (added.length) {
                              setFormFiles((prev) => ({
                                ...prev,
                                [f.key]: [...(prev[f.key] || []), ...added],
                              }));
                            }
                          } catch {
                            toast.error('The upload failed. The file was not attached.');
                          } finally {
                            setUploading(false);
                          }
                        }}
                        className="block w-full text-xs file:mr-3 file:rounded-md file:border file:border-slate-200 file:bg-slate-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold"
                      />
                      {uploading && <p className="text-[11px] text-slate-500">Uploading…</p>}
                      {f.key === 'paymentProofs' && (
                        <p className="text-[11px] text-slate-500">
                          {paymentProofRequired
                            ? 'Client payment proof is required before hand-off.'
                            : 'No proof needed for credit terms.'}
                        </p>
                      )}
                      {isEditingOpenHandoff && (formFiles[f.key] || []).length > 0 && (
                        <p className="text-[11px] text-amber-700">
                          Wrong screenshot or PDF? Use Replace (or Remove and attach the right one). The change goes to an Admin for approval with your reason.
                        </p>
                      )}
                      {(formFiles[f.key] || []).map((doc, i) => (
                        <div key={doc.url} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-2 py-1 text-xs">
                          <AttachmentPreviewButton url={doc.url} name={doc.name} />
                          <div className="flex shrink-0 items-center gap-2">
                            {/* Swap THIS file for the right one, in place. */}
                            <label className={`cursor-pointer text-blue-700 ${uploading ? 'pointer-events-none opacity-40' : ''}`}>
                              Replace
                              <input
                                type="file"
                                className="hidden"
                                accept={f.type === 'proof-files' ? 'image/png,image/jpeg,image/webp,application/pdf' : undefined}
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  e.target.value = '';
                                  if (!file) return;
                                  if (file.size > 50 * 1024 * 1024) { toast.error(`"${file.name}" is larger than 50 MB.`); return; }
                                  setUploading(true);
                                  try {
                                    const body = new FormData();
                                    body.append('file', file);
                                    const { data } = await api.post<{ url: string }>('/api/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
                                    if (data?.url) {
                                      setFormFiles((prev) => ({
                                        ...prev,
                                        [f.key]: (prev[f.key] || []).map((d, n) => (n === i ? { url: data.url, name: file.name } : d)),
                                      }));
                                      toast.success(`${doc.name} replaced with ${file.name}.`);
                                    }
                                  } catch {
                                    toast.error('The upload failed. The file was not replaced.');
                                  } finally {
                                    setUploading(false);
                                  }
                                }}
                              />
                            </label>
                            <button
                              type="button"
                              className="text-rose-600"
                              onClick={() => setFormFiles((prev) => ({
                                ...prev,
                                [f.key]: (prev[f.key] || []).filter((_, n) => n !== i),
                              }))}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : f.type === 'credit-amount' ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        {[100, 50, 40, 20].map((percent) => (
                          <button key={percent} type="button" className="rounded border px-2 py-1 text-xs"
                            onClick={() => setFormValues((prev) => ({ ...prev, creditAmount: (handoffTotal * percent / 100).toFixed(2) }))}>
                            {percent}%
                          </button>
                        ))}
                      </div>
                      <input aria-label="Credit amount" type="number" min="0" max={handoffTotal} step="0.01"
                        value={formValues.creditAmount ?? ''}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, creditAmount: e.target.value }))}
                        className="w-full rounded-lg border px-3 py-2 text-sm" />
                    </div>
                  ) : f.type === 'amount-derived' ? (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                      {!formValues.paymentTerms ? (
                        <span className="text-slate-400">Select the payment terms to see the amount received.</span>
                      ) : isCreditTerms(formValues) ? (
                        <p>On credit — nothing received yet. Order total <b>{money(handoffTotal)}</b>.</p>
                      ) : formValues.paymentTerms === 'partial' ? (
                        <p>Partial amount received: <b>{money(derivedAmountReceived)}</b> of {money(handoffTotal)}</p>
                      ) : (
                        <p>Full order total received: <b>{money(derivedAmountReceived)}</b></p>
                      )}
                      <p className="mt-1 text-[11px] text-slate-500">Balance remaining: {money(balanceAmount)}</p>
                    </div>
                  ) : f.type === 'packaging-preview' ? (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                      {packagingPreview.length === 0 ? (
                        <span className="text-slate-400">
                          No pack size on these products — Dispatch can set it on the challan.
                        </span>
                      ) : (
                        <>
                          <ul className="space-y-0.5">
                            {packagingPreview.map((l) => (
                              <li key={l.key} className="flex items-baseline justify-between gap-2 text-xs">
                                <span className="truncate text-slate-700">{l.description} × {l.quantity}</span>
                                <span className={cn(
                                  'shrink-0 font-semibold capitalize',
                                  l.packaging ? 'text-slate-900' : 'text-amber-600',
                                )}>
                                  {l.packaging || 'not set'}{l.packSize ? ` · ${l.packSize}` : ''}
                                </span>
                              </li>
                            ))}
                          </ul>
                          {/* Total pieces to physically load, by packaging type — request
                              #21: "no. of Pcs to be dispatched," not just what each line
                              individually ships in. */}
                          {packagingTotals.length > 0 && (
                            <p className="mt-2 border-t border-slate-200 pt-2 text-xs font-semibold text-slate-800">
                              Total to dispatch: {packagingTotals.map((t) => (
                                `${t.quantity} ${t.packaging.charAt(0).toUpperCase() + t.packaging.slice(1)}`
                              )).join(' · ')}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  ) : f.type === 'driver' ? (
                    <select
                      value={formValues[f.key] || ''}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="">
                        {drivers.length === 0 ? 'No driver free right now' : 'Assign later'}
                      </option>
                      {drivers.map((d) => (
                        <option key={d._id} value={d.userId}>
                          {d.name} — {d.vehicleNumber}{d.vehicleType ? ` (${d.vehicleType})` : ''}
                        </option>
                      ))}
                    </select>
                  ) : f.type === 'select' ? (
                    <select
                      value={formValues[f.key] || ''}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      {(f.options || [])
                        .filter((o) => !(f.key === 'transportMode' && o.value === 'in_house_driver' && formValues.transportMode !== 'in_house_driver'))
                        .map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                  ) : f.type === 'transporter' ? (
                    <div className="space-y-1.5">
                      <select
                        value={formValues[f.key] || ''}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      >
                        <option value="">Select transporter…</option>
                        {transporterOptions.map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                      <div className="flex items-center justify-between gap-2 text-xs">
                        {formValues[f.key] ? (
                          (() => {
                            const d = selectedTransporterDetails(formValues[f.key]);
                            return d
                              ? <span className="truncate text-slate-500" title={d.address}>{[d.phone, d.gstin && `GSTIN ${d.gstin}`].filter(Boolean).join(' · ')}</span>
                              : <span className="text-amber-700">No details yet — Accounts needs them for the e-way bill.</span>;
                          })()
                        ) : <span />}
                        <span className="flex shrink-0 gap-3">
                          {formValues[f.key] && (
                            <button type="button" className="font-semibold text-blue-700 hover:underline" onClick={() => setDefineTransporter(formValues[f.key])}>
                              Define
                            </button>
                          )}
                          <button type="button" className="font-semibold text-blue-700 hover:underline" onClick={() => setDefineTransporter('')}>
                            + New transporter
                          </button>
                        </span>
                      </div>
                    </div>
                  ) : (
                    <input
                      type={f.type}
                      value={formValues[f.key] || ''}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex shrink-0 justify-end gap-2 border-t border-slate-200 p-5 pt-3">
              <Button variant="ghost" size="sm" onClick={() => setOpenTarget(null)}>Cancel</Button>
              <Button
                size="sm"
                disabled={
                  extraInputSpec.fields.some((f) => fieldRequired(f) && !formValues[f.key]?.trim())
                  || (challanDialogOpen && formValues.paymentTerms === 'partial' && !(partialValue > 0 && partialValue < handoffTotal))
                  || (openTarget.to === 'delivery_challan' && formValues.transportMode === 'in_house_driver' && !formValues.driverId)
                  || (openTarget.to === 'delivery_challan' && formValues.transportMode === 'third_party_courier' && !formValues.courierName?.trim())
                  || (openTarget.to === 'delivery_challan' && paymentProofRequired && !hasPaymentProof)
                  || (challanDialogOpen && paymentArrangementValue === 'credit' && (!formValues.creditAmount?.trim() || !Number.isFinite(Number(formValues.creditAmount)) || Number(formValues.creditAmount) < 0 || Number(formValues.creditAmount) > handoffTotal))
                  // Not marked `required` on the field itself — that would also
                  // block the FIRST hand-off, where this field isn't even shown.
                  // The server only actually enforces this for a non-admin; an
                  // admin submitting blank still goes through there, same as
                  // leaving it blank here does nothing on a first hand-off.
                  || (isEditingOpenHandoff && !formValues.reason?.trim())
                  || run.isPending
                  // Confirming mid-upload would raise the document without the
                  // file that was being attached to it.
                  || uploading
                }
                onClick={() => {
                  // The local-transport details an edit was pre-filled with are
                  // Dispatch's, no longer on this form — never send them back.
                  // Re-sent, a Sales user's blank (hidden) amount wiped the one
                  // Admin had entered.
                  const values = Object.fromEntries(Object.entries(formValues)
                    .filter(([k]) => !(k.startsWith('localTransport') && k !== 'localTransportUsed')));
                  runNow(openTarget, { ...values, ...formFiles, amountReceived: derivedAmountReceived });
                }}
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
      {defineTransporter !== null && (
        <TransporterDefineModal
          name={defineTransporter}
          onClose={() => setDefineTransporter(null)}
          onSaved={(saved) => {
            // A new (or renamed) transporter is selected straight away.
            setFormValues((prev) => ({ ...prev, courierName: saved.name }));
            setDefineTransporter(null);
          }}
        />
      )}
    </>
  );
}
